import { PermissionPageKey } from '@utility/constants/permission';
import {
  BarcodeIcon,
  CheckSquareOffsetIcon,
  FactoryIcon,
  PackageIcon,
  PuzzlePieceIcon,
  ScrollIcon,
  ShoppingCartIcon,
  StorefrontIcon,
  HouseIcon,
  ClipboardTextIcon,
  CubeTransparentIcon,
  TreeStructureIcon,
  ProjectorScreenChartIcon,
  CopySimpleIcon,
  RectangleDashedIcon,
  NotEqualsIcon,
  KanbanIcon,
  ArrowSquareUpRightIcon,
  XSquareIcon,
  CheckSquareIcon,
  ArrowSquareLeftIcon,
  ChartLineUpIcon,
  ChartBarIcon,
  UserCircleIcon,
  FilesIcon,
  WarehouseIcon,
  TruckIcon,
  GearFineIcon,
  ReceiptIcon,
  CubeIcon,
} from '@phosphor-icons/react';
import { OrderStatus } from '@utility/constants/common';
import type { MenuNode } from './menu-permission';

export type SidebarMenu = MenuNode & {
  text: string;
  menus?: SidebarMenu[];
  phosphorIcon?: React.ReactNode;
  accordion?: boolean;
  navigateOnOpen?: boolean;
  navigateFirstMenu?: boolean;
  middleMenu?: null;
  titleAddButtonUrl?: string;
  rightText?: string | null;
  createPageLink?: string;
  count?: number;
  hidden?: boolean;
  lowerOpacity?: boolean;
  borderBottom?: boolean;
  /** Drawn as one row: its pages are switched between in the page itself. */
  collapseMenus?: boolean;
};

const orderIcons = {
  'Pending Shipment': <ArrowSquareUpRightIcon size={18} />,
  'Pending Fulfillment': <CheckSquareOffsetIcon size={18} />,
  Canceled: <XSquareIcon size={18} />,
  Shipped: <CheckSquareIcon size={18} />,
  Returns: <ArrowSquareLeftIcon size={18} />,
};

const customOrder = [
  OrderStatus.PendingShipment,
  OrderStatus.PendingFulfillment,
  OrderStatus.Shipped,
  OrderStatus.Canceled,
  OrderStatus.Returns,
];

// THIS IS FOR SIDEBAR
export function sidebarMenus(
  user,
  metadata,
  data,
  scmMenu,
  catalogPermission,
  wholeSalePermission,
) {
  // ------- ORDER ---------

  const order: SidebarMenu = {
    text: 'Orders',
    key: PermissionPageKey.Orders,
    link: '/app/orders/list/Pending%20Shipment/',
    // Order detail, clone and refund routes have no menu entry of their own.
    routeRoot: '/app/orders',
    phosphorIcon: <ShoppingCartIcon size={18} />,
    titleAddButtonUrl: '/app/orders/list/Pending%20Shipment/?createPopup=true',
    rightText: null,
    accordion: false,
    navigateOnOpen: true,
    middleMenu: null,
    navigateFirstMenu: true,
    /* One row: the list's own status tabs move between the statuses. */
    collapseMenus: true,
    permission: user.permissions[PermissionPageKey.Orders]?.permission,
  };

  const sortedStatus = Object.values(metadata.orderStatus).sort(
    // @ts-expect-error - TS18046: 'a' is of type 'unknown'.
    (a, b) => customOrder.indexOf(a.key) - customOrder.indexOf(b.key),
  );

  order.menus = sortedStatus.map(item => {
    return {
      // @ts-expect-error - TS2698: Spread types may only be created from object types.
      ...item,
      borderBottom: [OrderStatus.Shipped, OrderStatus.Returns].includes(
        // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
        item.key,
      ),
      // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
      phosphorIcon: orderIcons[item.key],
      /* @ts-expect-error - TS18046: 'item' is of type 'unknown'. */
      link: `/app/orders/list/${encodeURIComponent(item.key)}`,
      // @ts-expect-error - TS18046: 'item' is of type 'unknown'.
      permission: user.permissions[PermissionPageKey.Orders]?.modules[item.key],
    };
  });

  const temp: SidebarMenu[] = [order];

  // ------- INVENTORY ---------

  temp.push({
    text: 'Inventory',
    key: PermissionPageKey.SCM,
    // @ts-expect-error - TS2322: Type 'string | null' is not assignable to type 'string'.
    link:
      scmMenu?.permissionKey === PermissionPageKey.Dashboard
        ? '/app/scm'
        : null,
    phosphorIcon: <PackageIcon size={18} />,
    accordion: true,
    navigateOnOpen: scmMenu?.permissionKey === PermissionPageKey.Dashboard,
    permission: scmMenu?.permission,
    menus: [
      {
        text: 'Storage Providers',
        phosphorIcon: <WarehouseIcon size={18} />,
        link: '/app/scm/storage-provider',
        genericLink: '/app/scm/storage-provider',
        key: PermissionPageKey.SP,
        permission:
          user.permissions[PermissionPageKey.SCM].modules?.[
            PermissionPageKey.SP
          ]?.permission,
      },
      {
        // EN-2766: the read-only list of every shipment type.
        text: 'Shipments',
        phosphorIcon: <TruckIcon size={18} />,
        link: '/app/shipments',
        genericLink: '/app/shipments',
        key: PermissionPageKey.Shipments,
        permission:
          user.permissions[PermissionPageKey.SCM].modules[
            PermissionPageKey.Shipments
          ],
      },
      {
        text: 'Work Orders',
        phosphorIcon: <GearFineIcon size={18} />,
        link: '/app/scm/work-orders',
        genericLink: '/app/scm/work-orders',
        key: PermissionPageKey.WO,
        createPageLink: '/app/scm/work-orders/create',
        permission:
          user.permissions[PermissionPageKey.SCM].modules[PermissionPageKey.WO],
      },
    ],
  });

  // ------------ CATALOG -------------

  const catalog: SidebarMenu = {
    text: 'Catalog',
    key: PermissionPageKey.Catalog,
    link: '/app/catalogs/catalog',
    // /app/catalogs is a route but not a menu entry; it resolves to the first
    // catalog page below it.
    parentLink: '/app/catalogs',
    accordion: true,
    /* One row: the sidebar's main list draws only collapsed groups. */
    collapseMenus: true,
    phosphorIcon: <BarcodeIcon size={18} />,
    navigateOnOpen: true,
    menus: [
      {
        text: 'Products',
        phosphorIcon: <CubeIcon size={18} />,
        link: '/app/catalogs/catalog',
        genericLink: '/app/catalogs/catalog',
        key: PermissionPageKey.Catalog,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Catalog
          ] || catalogPermission,
      },
      {
        text: 'Components',
        phosphorIcon: <PuzzlePieceIcon size={18} />,
        link: '/app/catalogs/components',
        key: PermissionPageKey.Components,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Components
          ] || catalogPermission,
      },
      {
        text: 'Carton Configuration',
        link: '/app/catalogs/attributes',
        phosphorIcon: <CubeTransparentIcon size={18} />,
        key: PermissionPageKey.Attributes,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Attributes
          ] || catalogPermission,
      },
      {
        phosphorIcon: <TreeStructureIcon size={18} />,
        text: 'Classification Guide',
        link: '/app/catalogs/classification-guide',
        key: PermissionPageKey.ClassificationGuide,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.ClassificationGuide
          ] || catalogPermission,
      },
      {
        phosphorIcon: <CopySimpleIcon size={18} />,
        text: 'Duplicate Listings',
        link: '/app/catalogs/duplicate-listings',
        count: data?.merge_conflict,
        hidden: data?.merge_conflict === 0,
        lowerOpacity: !data?.merge_conflict,
        key: PermissionPageKey.ClassificationGuide,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Catalog
          ] || catalogPermission,
      },
      {
        phosphorIcon: <RectangleDashedIcon size={18} />,
        text: 'Missing Information',
        link: '/app/catalogs/resolve-conflict',
        key: PermissionPageKey.ClassificationGuide,
        lowerOpacity: !data?.catalog_conflict,
        count: data?.catalog_conflict,
        hidden: data?.catalog_conflict === 0,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Catalog
          ] || catalogPermission,
      },
      {
        phosphorIcon: <NotEqualsIcon size={18} />,
        text: 'Unmatched Listings',
        link: '/app/catalogs/listings/unmatched-items',
        key: PermissionPageKey.ClassificationGuide,
        count: data?.unmappedListings,
        permission:
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Catalog
          ] || catalogPermission,
      },
    ],

    permission: catalogPermission,
    middleMenu: null,
  };

  temp.push(catalog);

  // -------- PURCHASING -------

  temp.push({
    text: 'Purchasing',
    link: '/app/dashboard',
    phosphorIcon: <FactoryIcon size={18} />,
    key: PermissionPageKey.Dashboard,
    permission: user.permissions[PermissionPageKey.Dashboard]?.permission,
    menus: [
      {
        phosphorIcon: <KanbanIcon size={18} />,
        text: 'Dashboard',
        link: '/app/scm',
        key: PermissionPageKey.SCM,
        permission:
          user.permissions[PermissionPageKey.SCM].modules[
            PermissionPageKey.Dashboard
          ],
      },
      {
        text: 'Suppliers',
        phosphorIcon: <FactoryIcon size={18} />,
        link: '/app/scm/supplier',
        genericLink: '/app/scm/supplier',
        key: PermissionPageKey.Suppliers,
        permission:
          user.permissions[PermissionPageKey.SCM].modules[
            PermissionPageKey.Suppliers
          ],
      },
      {
        phosphorIcon: <ReceiptIcon size={18} />,
        text: 'Purchase Orders',
        link: '/app/scm/purchase-orders',
        genericLink: '/app/scm/purchase-orders',
        key: PermissionPageKey.PO,
        createPageLink: '/app/scm/purchase-orders/create',
        permission:
          user.permissions[PermissionPageKey.SCM].modules[PermissionPageKey.PO],
      },
      {
        text: 'Forecast',
        phosphorIcon: <ChartLineUpIcon size={18} />,
        link: '/app/scm/restock',
        key: PermissionPageKey.Restock,
        permission:
          user.permissions[PermissionPageKey.SCM].modules?.[
            PermissionPageKey.Restock
          ] ||
          user.permissions[PermissionPageKey.Catalog].modules?.[
            PermissionPageKey.Restock
          ] ||
          catalogPermission,
      },
    ],
  });

  // ----------- WHOLESALE -------------

  temp.push({
    text: 'Wholesale',
    accordion: true,
    key: PermissionPageKey.Wholesale,
    link: '/app/wholesale/wholesalers',
    // An invoice's detail route sits under /app/wholesale/invoices, which is
    // shorter than the menu entry's own link.
    parentLink: '/app/wholesale',
    navigateOnOpen: true,
    phosphorIcon: <StorefrontIcon size={18} />,
    permission: wholeSalePermission,
    menus: [
      {
        text: 'Vendors',
        link: '/app/wholesale/wholesalers',
        genericLink: '/app/wholesale/wholesalers',
        key: PermissionPageKey.Wholesale,
        phosphorIcon: <StorefrontIcon size={18} />,
        permission:
          user.permissions[PermissionPageKey.Wholesale]?.modules?.[
            PermissionPageKey.Wholesalers
          ] || wholeSalePermission,
      },
      {
        text: 'Orders',
        link: '/app/wholesale/invoices/list/all',
        genericLink: '/app/wholesale/invoices',
        phosphorIcon: <ScrollIcon size={18} />,
        key: PermissionPageKey.Invoices,
        createPageLink: '/app/wholesale/invoices/create',
        permission:
          user.permissions[PermissionPageKey.Wholesale]?.modules?.[
            PermissionPageKey.Invoices
          ] || wholeSalePermission,
      },
    ],
  });

  // ---------- REPORTS ---------------

  const dataStudio: SidebarMenu = {
    text: 'Reports',
    key: PermissionPageKey.DataStudio,
    phosphorIcon: <ChartBarIcon size={18} />,
    accordion: true,
    permission: user.permissions[PermissionPageKey.DataStudio]?.permission,
    link: `/app/dashboard/awaiting-actions`,

    menus: [
      {
        phosphorIcon: <HouseIcon size={18} />,
        text: 'Dashboard',
        link: `/app/dashboard/awaiting-actions`,
        key: PermissionPageKey.Dashboard,
        permission:
          user.permissions[PermissionPageKey.Dashboard]?.settings?.[
            PermissionPageKey.AwaitingActions
          ],
      },
      {
        phosphorIcon: <ClipboardTextIcon size={18} />,
        text: 'Audit Logs',
        link: '/app/dashboard/logs',
        key: PermissionPageKey.Dashboard,
        permission:
          user.permissions[PermissionPageKey.Dashboard]?.settings?.[
            PermissionPageKey.Logs
          ],
      },
      {
        phosphorIcon: <ProjectorScreenChartIcon size={18} />,
        text: 'Analytics',
        link: '/app/data-studio/analytics',
        key: PermissionPageKey.Dashboard,
        permission:
          user.permissions[PermissionPageKey.Dashboard]?.settings?.[
            PermissionPageKey.Analytics
          ],
      },
      {
        text: 'Customers',
        link: '/app/crm',
        genericLink: '/app/crm',
        phosphorIcon: <UserCircleIcon size={18} />,
        key: PermissionPageKey.CRM,
        permission: user.permissions[PermissionPageKey.CRM]?.permission,
      },
      {
        text: 'Inventory Report',
        link: '/app/data-studio/inventory-report',
        key: PermissionPageKey.InventoryReport,
        phosphorIcon: <FilesIcon size={18} />,
        permission:
          user.permissions[PermissionPageKey.DataStudio]?.modules?.[
            PermissionPageKey.InventoryReport
          ],
      },
      {
        text: 'Manifest Reports',
        link: '/app/data-studio/manifest-reports',
        key: PermissionPageKey.InventoryReport,
        phosphorIcon: <FilesIcon size={18} />,
        permission:
          user.permissions[PermissionPageKey.DataStudio]?.modules?.[
            PermissionPageKey.InventoryReport
          ],
      },
    ],
  };
  temp.push(dataStudio);

  return temp;
}
